import React, { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import { View, ScrollView, Text } from 'react-native';
import { useTheme } from '@themeProvider';
import { useLanguage } from '@languageProvider';
import { Spacing, Commons, Typography, Colors } from '@styles';
import { PromotionCard } from '@molecules';
import { Promotions } from '@services';
import { Promotion } from '@datamodel';
import { TextInput, Button } from '@atoms';
import { useSnackbar } from '@lib';

const styles = Commons.styles;

export const PromotionsDrawer = forwardRef(({ style, promotionPress, tracker, promotions={}, place, addPromotions, ...props }, ref) => {
    const textRef = useRef();
    const { translate } = useLanguage();
    const { useColor } = useTheme();
    const { pushSnack } = useSnackbar();
    const [ code, setCode ] = useState('');

    const [title, secondary, line] = useColor(['title', 'secondary', 'line']);

    useImperativeHandle(ref, () => ({
        focusTextInput: () => {
            if (textRef.current) {
                textRef.current.focus();
            }
        }
    }));
    
    const sendCode = async () => {
        if (code?.length < 1 ) return;
        try {            
            const { promotion } = await Promotions.subscribe({
                code,
                place: place.placeId
            })

            addPromotions({
                [promotion.promotionId]: promotion
            })

            pushSnack({
                text: translate('promotionAdded'),
                icon: 'discount',
                type: 'success'
            })

            setCode('');

            if (props.screenTracker) {
                props.screenTracker.tap('SendCodeButton', {
                    action: 'sendValidCode',
                    code
                })
            }

        } catch (error) {
            if (error.statusCode === 404 ) {
                pushSnack({
                    text: translate('invalidCode'),
                    type: 'warning',
                    icon: 'warning'
                })

                if (props.screenTracker) {
                    props.screenTracker.tap('SendCodeButton', {
                        action: 'sendInvalidCode',
                        code
                    })
                }

            } else {
                pushSnack({
                    text: translate('somethingWentWrong'),
                    type: 'warning',
                    icon: 'warning'
                })

                setCode('');
            }
        }
    };

    return <View {...props} style={[style]}>
        <Text style={[Typography.TITLE, { margin: Spacing.M, marginBottom: Spacing.XS, color: title, textAlign: 'center'}]}>{translate('haveACode?')}</Text>
        <TextInput
            ref={textRef}
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect={false}
            enablesReturnKeyAutomatically={true}
            onSubmitEditing={sendCode}
            returnKeyType="send"
            selectionColor={Colors.setOpacity(secondary, 0.2)}
            centered label={translate('discountCode')} textStyle={[styles.noOutline, { textAlign: 'center' }]} style={{ marginHorizontal: Spacing.M, }} {...{ value: code, setValue: setCode }}/>
        <Button loadOnPress onPress={sendCode} disabled={code?.length < 1} status={code.length < 1 ? 'disabled' : 'default'} title={translate('send')} color="secondary" style={{ margin: Spacing.M }} />

        { Object.keys(promotions).length > 0 &&
            <>
            <Text style={[Typography.TITLE, { margin: Spacing.M, marginBottom: Spacing.XS, color: title }]}>{translate('promotions')}</Text>
            <ScrollView
                scrollEventThrottle={0}
                onScroll={() => {
                    tracker.scroll('PromotionsDrawerScrollView');
                }}
                contentContainerStyle={{ paddingHorizontal: Spacing.M }}
            >
                {
                    Object.keys(promotions).map( id => <PromotionCard
                        onPress={() => promotionPress(promotions[id])}
                        style={{ marginBottom: Spacing.XS }}
                        key={id}
                        promotion={promotions[id]}
                        place={place.placeId === Promotion.getPlaceId(promotions[id]) ? place : null}/> )
                }
            </ScrollView>
            </>
        }
    </View>
})
